import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useEffect } from 'react';
import { Minimize2, Maximize2, History, X } from 'lucide-react';
import { useWidgetEditable, WidgetWrapper } from '@brandclub/bc-widgets';

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl) => {
    // Check if already loaded
    const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
    if (existingLink)
        return;
    // Inject into document.head (works with Shadow DOM)
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    document.head.appendChild(link);
};
const defaultState = {
    // Text content
    titleContent: 'CONCIERGE',
    titleFontSize: 14,
    titleFontWeight: 'bold',
    titleFontFamily: 'inherit',
    titleColor: '#031425',
    titleLetterSpacing: 2,
    titleTextTransform: 'uppercase',
    titleTextAlign: 'center',
    titleLineHeight: 1.0,
    // Button colors - default to primary color (dark blue-gray)
    closeButtonColor: '#031425',
    maximizeButtonColor: '#031425',
    historyButtonColor: '#031425',
};
const editFields = [
    // Text content
    { key: 'titleContent', label: 'Title Text', type: 'text' },
    {
        key: 'titleFontSize',
        label: 'Title Font Size (px)',
        type: 'number',
        min: 10,
        max: 24,
        step: 1,
    },
    {
        key: 'titleFontWeight',
        label: 'Title Font Weight',
        type: 'options',
        options: [
            { label: 'Normal', value: 'normal' },
            { label: 'Medium', value: 'medium' },
            { label: 'Semibold', value: 'semibold' },
            { label: 'Bold', value: 'bold' },
        ],
    },
    {
        key: 'titleFontFamily',
        label: 'Title Font Family',
        type: 'font',
    },
    { key: 'titleColor', label: 'Title Color', type: 'color' },
    {
        key: 'titleLetterSpacing',
        label: 'Title Letter Spacing (px)',
        type: 'number',
        min: -2,
        max: 5,
        step: 0.5,
    },
    {
        key: 'titleTextTransform',
        label: 'Title Text Transform',
        type: 'options',
        options: [
            { label: 'None', value: 'none' },
            { label: 'Uppercase', value: 'uppercase' },
            { label: 'Lowercase', value: 'lowercase' },
            { label: 'Capitalize', value: 'capitalize' },
        ],
    },
    {
        key: 'titleTextAlign',
        label: 'Title Text Alignment',
        type: 'options',
        options: [
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
        ],
    },
    {
        key: 'titleLineHeight',
        label: 'Title Line Height',
        type: 'number',
        min: 0.8,
        max: 2.0,
        step: 0.1,
    },
    // Button colors
    { key: 'closeButtonColor', label: 'Close Button Color', type: 'color' },
    { key: 'maximizeButtonColor', label: 'Maximize Button Color', type: 'color' },
    { key: 'historyButtonColor', label: 'History Button Color', type: 'color' },
];
const getFontWeightValue = (weight) => {
    const weightMap = {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    };
    return weightMap[weight] || 400;
};
const ConciergeWidgetHeader = ({ showMaximize = true, isMaximized = false, onMaximize, onMinimize, onClose, onHistoryClick, title = 'CONCIERGE', widgetId = 'concierge-widget-header', displayName = 'Concierge Header', initialState = {}, }) => {
    const { state, isLoading, isSaving, handleEditChange, handleSave, themeColors, themeFonts, resolveColor, canSave } = useWidgetEditable(widgetId, Object.assign(Object.assign(Object.assign({}, defaultState), { titleContent: (title === null || title === void 0 ? void 0 : title.toUpperCase()) || defaultState.titleContent }), initialState), editFields);
    // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
    const fontKey = state.titleFontFamily;
    const fontData = fontKey && fontKey !== 'inherit' ? themeFonts[fontKey] : null;
    // For Google Fonts, we need to load via <link> tag in document.head
    const googleFontLink = fontData && fontData.format === 'google' ? fontData.url : null;
    // Load Google Font into document.head when font changes
    useEffect(() => {
        if (googleFontLink) {
            loadGoogleFontInHead(googleFontLink);
        }
    }, [googleFontLink]);
    // Show loading state while fetching theme config
    if (isLoading) {
        return (jsx("div", { className: "tw:flex tw:items-center tw:justify-between tw:p-4 tw:border-b tw:border-gray-200 tw:bg-white", "data-concierge-header": true }));
    }
    // Determine font-family CSS based on font type
    const fontFamilyCss = fontData
        ? fontData.format === 'system'
            ? fontData.url
            : `"${fontData.name}", sans-serif`
        : 'inherit';
    // Determine if we need @font-face (only for uploaded fonts)
    const needsFontFace = fontData && fontData.format !== 'system' && fontData.format !== 'google';
    // Generate unique CSS class for this widget instance
    const widgetClass = `concierge-header-${widgetId || 'default'}`;
    // Calculate hover color (70% opacity of base color) for CSS
    const getHoverColor = (color) => {
        // If color is hex, convert to rgba with 70% opacity
        if (color.startsWith('#')) {
            const hex = color.slice(1);
            // Handle both 3-digit and 6-digit hex
            const r = hex.length === 3 ? parseInt(hex[0] + hex[0], 16) : parseInt(hex.slice(0, 2), 16);
            const g = hex.length === 3 ? parseInt(hex[1] + hex[1], 16) : parseInt(hex.slice(2, 4), 16);
            const b = hex.length === 3 ? parseInt(hex[2] + hex[2], 16) : parseInt(hex.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, 0.7)`;
        }
        // If already rgba, try to modify opacity
        if (color.startsWith('rgba')) {
            return color.replace(/,\s*[\d.]+\)$/, ', 0.7)');
        }
        // If rgb, convert to rgba
        if (color.startsWith('rgb')) {
            return color.replace('rgb', 'rgba').replace(')', ', 0.7)');
        }
        // Fallback: return as-is
        return color;
    };
    const widgetContent = (jsxs(Fragment, { children: [jsx("style", { children: `
        ${needsFontFace
                    ? `
          @font-face {
            font-family: "${fontData.name}";
            src: url("${fontData.url}") format("${fontData.format}");
            font-display: swap;
          }
        `
                    : ''}
        ${fontFamilyCss !== 'inherit'
                    ? `.${widgetClass} [data-title-text] { font-family: ${fontFamilyCss} !important; }`
                    : ''}
        .${widgetClass} .concierge-maximize-icon {
          color: ${resolveColor(state.maximizeButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-maximize-icon:hover {
          color: ${getHoverColor(resolveColor(state.maximizeButtonColor))};
        }
        .${widgetClass} .concierge-close-icon {
          color: ${resolveColor(state.closeButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-close-icon:hover {
          color: ${getHoverColor(resolveColor(state.closeButtonColor))};
        }
        .${widgetClass} .concierge-history-icon {
          color: ${resolveColor(state.historyButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-history-icon:hover {
          color: ${getHoverColor(resolveColor(state.historyButtonColor))};
        }
      ` }), jsxs("div", Object.assign({ className: `tw:flex tw:items-center tw:justify-between tw:p-4 tw:border-b tw:border-gray-200 tw:bg-white ${widgetClass}`, "data-concierge-header": true }, { children: [jsxs("div", Object.assign({ className: "tw:flex tw:items-center tw:gap-1", style: { minWidth: '80px' } }, { children: [isMaximized && onMinimize && (jsx("button", Object.assign({ onClick: onMinimize, className: "tw:p-2 tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group", "aria-label": "Minimize to widget" }, { children: jsx(Minimize2, { className: "tw:w-5 tw:h-5 concierge-maximize-icon" }) }))), !isMaximized && showMaximize && onMaximize && (jsx("button", Object.assign({ onClick: onMaximize, className: "tw:p-2 tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group", "aria-label": "Expand to full view" }, { children: jsx(Maximize2, { className: "tw:w-5 tw:h-5 concierge-maximize-icon" }) })))] })), jsx("span", Object.assign({ "data-title-text": true, style: {
                            fontSize: `${state.titleFontSize}px`,
                            fontWeight: getFontWeightValue(state.titleFontWeight),
                            fontFamily: fontFamilyCss !== 'inherit' ? fontFamilyCss : undefined,
                            color: resolveColor(state.titleColor),
                            letterSpacing: `${state.titleLetterSpacing}px`,
                            textTransform: state.titleTextTransform,
                            textAlign: 'center',
                            lineHeight: state.titleLineHeight,
                            flex: 1,
                        } }, { children: state.titleContent })), jsxs("div", Object.assign({ className: "tw:flex tw:items-center tw:gap-1 tw:justify-end", style: { minWidth: '80px' } }, { children: [onHistoryClick && (jsx("button", Object.assign({ onClick: onHistoryClick, className: "tw:p-2 tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group", "aria-label": "View chat history" }, { children: jsx(History, { className: "tw:w-5 tw:h-5 concierge-history-icon" }) }))), jsx("button", Object.assign({ onClick: onClose, className: "tw:p-2 tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group", "aria-label": "Close" }, { children: jsx(X, { className: "tw:w-5 tw:h-5 concierge-close-icon" }) }))] }))] }))] }));
    // Wrap with WidgetWrapper for edit capability
    return (jsx(WidgetWrapper, Object.assign({ widgetId: widgetId, displayName: displayName, editFields: editFields, editValues: state, onEditChange: handleEditChange, onSave: canSave ? handleSave : undefined, isSaving: isSaving, themeColors: themeColors, themeFonts: themeFonts }, { children: widgetContent })));
};

export { ConciergeWidgetHeader };
//# sourceMappingURL=ConciergeWidgetHeader.js.map
